// Browser-side background-noise suppression for the softphone mic.
//
// Runs RNNoise (a small ML denoiser, ~85KB WASM) inside an AudioWorklet on the
// agent's outbound mic stream BEFORE it reaches JsSIP. Unlike the browser's
// built-in `noiseSuppression` constraint — which is tuned for steady noise
// (fans, hum) — RNNoise also attenuates non-stationary noise such as other
// people talking nearby in an open call-center floor.
//
// Design contract: this NEVER blocks a call. If the WASM/Worklet can't load
// (old browser, blocked asset, missing SIMD), `process()` falls back to the
// original stream so the agent can still dial. The fallback is logged, not
// thrown.

// The RNNoise library + its WASM/worklet assets are loaded lazily (dynamic
// import) the first time suppression actually runs. This keeps them out of the
// main bundle AND off the import-time path — the library references
// AudioWorkletNode at module top-level, which doesn't exist in jsdom/SSR, so a
// static import would crash test collection.

/** Result of suppressing a stream: the (possibly filtered) stream + cleanup. */
export interface SuppressedStream {
  /** The stream to hand to JsSIP. Filtered when active, original on fallback. */
  stream: MediaStream;
  /** True when RNNoise is actually filtering; false when we fell back. */
  active: boolean;
  /** Tear down the AudioContext + worklet. Safe to call multiple times. */
  dispose: () => void;
}

// The library module + WASM binary are fetched once and reused across calls.
// AudioContexts are per-call (they carry the live graph) but the compiled
// denoiser and the imported module are shared.
type RnnoiseModule = typeof import('@sapphi-red/web-noise-suppressor');
let modulePromise: Promise<RnnoiseModule> | null = null;
let wasmBinaryPromise: Promise<ArrayBuffer> | null = null;

function getModule(): Promise<RnnoiseModule> {
  if (!modulePromise) {
    modulePromise = import('@sapphi-red/web-noise-suppressor');
  }
  return modulePromise;
}

async function getWasmBinary(mod: RnnoiseModule): Promise<ArrayBuffer> {
  if (!wasmBinaryPromise) {
    // Asset URLs are resolved lazily too so jsdom never touches them at import.
    const [{ default: wasmUrl }, { default: simdUrl }] = await Promise.all([
      import('@sapphi-red/web-noise-suppressor/rnnoise.wasm?url'),
      import('@sapphi-red/web-noise-suppressor/rnnoise_simd.wasm?url'),
    ]);
    wasmBinaryPromise = mod.loadRnnoise({ url: wasmUrl, simdUrl });
  }
  return wasmBinaryPromise;
}

/**
 * Wrap a raw mic MediaStream with RNNoise suppression.
 *
 * @param input  the raw stream from getUserMedia (must contain an audio track)
 * @returns      a SuppressedStream — `.stream` is what you give to JsSIP
 *
 * On any failure the original `input` is returned with `active: false`, so the
 * caller can use the result unconditionally.
 */
export async function suppressNoise(
  input: MediaStream,
): Promise<SuppressedStream> {
  // No-op safety net: if the platform lacks the Web Audio bits we need, bail to
  // the raw stream rather than throwing.
  if (
    typeof AudioContext === 'undefined' ||
    typeof AudioWorkletNode === 'undefined' ||
    input.getAudioTracks().length === 0
  ) {
    return {
      stream: input,
      active: false,
      dispose: () => input.getTracks().forEach((t) => t.stop()),
    };
  }

  let ctx: AudioContext | null = null;
  try {
    const mod = await getModule();
    const wasmBinary = await getWasmBinary(mod);
    const { default: workletUrl } = await import(
      '@sapphi-red/web-noise-suppressor/rnnoiseWorklet.js?url'
    );

    // RNNoise ASSUMES a 48kHz sample rate (it works on 480-sample / 10ms
    // frames at 48kHz). If we let the browser pick the device default (often
    // 44.1kHz), the worklet still runs but its output is pitch/timing-distorted
    // — that's the "can't hear the agent well" symptom. Pin the context to
    // 48kHz; if the platform refuses, the throw drops us to the raw-mic
    // fallback below rather than shipping garbled audio.
    ctx = new AudioContext({ sampleRate: 48000 });
    // The context can start 'suspended' under autoplay policy; a suspended
    // graph passes silence/garbage. Resume it before wiring audio. (We're
    // already inside a user gesture — the agent clicked dial/answer.)
    if (ctx.state === 'suspended') await ctx.resume();
    await ctx.audioWorklet.addModule(workletUrl);

    const source = ctx.createMediaStreamSource(input);
    const rnnoise = new mod.RnnoiseWorkletNode(ctx, {
      maxChannels: 1, // telephony is mono; keeps the worklet cheap
      wasmBinary,
    });
    const destination = ctx.createMediaStreamDestination();

    source.connect(rnnoise).connect(destination);

    // Carry over the original track's enabled state and id semantics: the
    // denoised track is what travels over RTP; the mic stays the source.
    const stream = destination.stream;

    const dispose = () => {
      try {
        source.disconnect();
        rnnoise.disconnect();
        destination.disconnect();
      } catch {
        /* graph already torn down */
      }
      // Stop the raw mic tracks — they belong to this call only.
      input.getTracks().forEach((t) => t.stop());
      if (ctx && ctx.state !== 'closed') void ctx.close();
      ctx = null;
    };

    return { stream, active: true, dispose };
  } catch (err) {
    // Never let denoising failure kill a call — fall back to the raw mic.
    console.warn('[noiseSuppressor] RNNoise unavailable, using raw mic:', err);
    if (ctx && ctx.state !== 'closed') void ctx.close();
    return {
      stream: input,
      active: false,
      dispose: () => input.getTracks().forEach((t) => t.stop()),
    };
  }
}

/** Reset the cached module + WASM (tests). */
export function __resetNoiseSuppressorCache(): void {
  modulePromise = null;
  wasmBinaryPromise = null;
}
